import { rem } from 'polished'
import React, { PropsWithChildren, useContext } from 'react'
import styled from 'styled-components'

import SavedItemRemoveButton from '../ItemCard/Common/SavedItemRemoveButton'
import { ItemCardVariant } from '../ItemCard/ItemCard'
import { SavedBottomContent } from '../ItemCard/SavedBottomContent'
import HeadingFullPrint from '../util/HeadingFullPrint'

import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBookmarkIcon from 'components/Luxkit/Icons/line/LineBookmarkIcon'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import NoPrint from 'components/utils/NoPrint'
import PrintOnly from 'components/utils/PrintOnly'
import { verticalChildSpacing } from 'components/utils/spacing'
import { EmptyArray, nonNullable } from 'lib/arrayUtils'
import { memoWithNestedProps } from 'lib/memoWithNestedProps'
import noop from 'lib/noop'
import zIndex from 'styles/tools/z-index'
import { DesktopLayoutSizes } from 'tripPlanner/components/ItemCard/Common/utils'
import { ItemImageContainer } from 'tripPlanner/components/ItemCard/ItemImageContainer'
import BaseSavedOffer from 'tripPlanner/components/SavedOffers/BaseSavedOffer'
import TripContext from 'tripPlanner/contexts/TripContext'
import { ItemMetadata } from 'tripPlanner/models'
import { TripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const Content = styled.div`
  position: relative;
  flex: 1;
  padding: ${rem(20)};
  border: 1px dashed ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${(props) => props.theme.borderRadius.large};

  @media print {
    border-top: 1px dashed ${({ theme }) => theme.palette.neutral.five};
  }

  @media not print {
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    ${DesktopLayoutSizes} {
      border-top: 1px dashed ${({ theme }) => theme.palette.neutral.five};
      border-left: 0;
      border-top-right-radius: ${(props) => props.theme.borderRadius.large};
      border-bottom-left-radius: 0;
    }
  }
`

const SavedButtonContainer = styled.div`
  position: absolute;
  right: ${rem(16)};
  top: 0;
  visibility: hidden;

  ${DesktopLayoutSizes} {
    visibility: unset;
    top: ${rem(16)};
    transform: unset;
    z-index: ${zIndex.floatingBookmarkButton};
  }
`

const MainContent = styled.div`
  flex: 1;
  ${verticalChildSpacing[8]}
`

const StyledTileInclusions = styled(OfferTileInclusions)`
  margin-top: ${rem(8)};
`

interface Props {
  item: TripItem
  name: string
  imgId?: string
  imgSrc?: string
  placeId?: string
  descriptors?: Array<string | undefined>
  /** Alternate `descriptors` array to use for print view */
  printDescriptors?: Array<string | undefined>
  locationDescription?: React.ReactNode
  imageOverlayContent?: React.ReactNode
  priceAreaContent?: React.ReactNode
  ctaAreaContent?: React.ReactNode
  urgencyLabels?: React.ReactNode
  to?: string
  isSoldOut?: boolean
  testId?: string
  /** Disables click handler, link and menu */
  nonInteractable?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
  inclusions?: App.TileInclusion[]
  offerURL?: string
}

function SavedOfferCard({
  item,
  name,
  imageOverlayContent,
  imgId,
  imgSrc,
  locationDescription,
  placeId,
  priceAreaContent,
  ctaAreaContent,
  urgencyLabels,
  to,
  isSoldOut,
  descriptors = EmptyArray,
  printDescriptors = EmptyArray,
  testId,
  nonInteractable = false,
  onClick,
  onClickTripName = noop,
  variant,
  inclusions,
  offerURL,
}: PropsWithChildren<Props>) {
  const { type } = item
  const Icon = ItemMetadata[type].icon

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const descriptorsString = nonNullable(descriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  const printDescriptorsString = nonNullable(printDescriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  // If we have different descriptors for print view, only show the normal ones in non-print view
  const DescriptorsWrapper = printDescriptorsString ? NoPrint : React.Fragment
  // We don't want to display trip name if we're in a trip
  return (
    <BaseSavedOffer
      to={to}
      tripId={item.tripId}
      openIn={canEdit ? 'immersiveMode' : 'newWindow'}
      isSoldOut={isSoldOut}
      sectionId={itemSectionId(item.id)}
      testId={testId}
      nonInteractable={nonInteractable}
      onClick={onClick}
    >
      <ItemImageContainer
        item={item}
        name={name}
        imgId={imgId}
        imgSrc={imgSrc}
        placeId={placeId}
        imageOverlayContent={
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <div>{imageOverlayContent}</div>
            {!nonInteractable && canEdit && (
              <>
                <CSSBreakpoint max="mobile">
                  <SavedItemRemoveButton item={item} />
                </CSSBreakpoint>
                <CSSBreakpoint only="desktop">
                  <SavedItemRemoveButton item={item} />
                </CSSBreakpoint>
              </>
            )}
          </Group>
        }
      />
      <Content>
        <CSSBreakpoint min="tablet">
          <SavedButtonContainer>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {isSoldOut && (
                <StatusLabel variant="cancelled">Sold Out</StatusLabel>
              )}
              {!nonInteractable && canEdit && (
                <SavedItemRemoveButton item={item} />
              )}
            </Group>
          </SavedButtonContainer>
        </CSSBreakpoint>

        <MainContent>
          {!isSoldOut && urgencyLabels && <NoPrint>{urgencyLabels}</NoPrint>}
          <Group
            direction="horizontal"
            gap={8}
            verticalAlign="center"
            horizontalAlign="space-between"
          >
            <BodyTextBlock variant="small" startIcon={<Icon size="XS" />}>
              {ItemMetadata[type].singularName}
            </BodyTextBlock>
            {isSoldOut && (
              <CSSBreakpoint max="mobile">
                <StatusLabel variant="cancelled">Sold Out</StatusLabel>
              </CSSBreakpoint>
            )}
          </Group>
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              {locationDescription}
              <HeadingFullPrint variant="heading5" lineClamp={2}>
                {name}
              </HeadingFullPrint>
              {inclusions && inclusions.length > 0 && (
                <StyledTileInclusions
                  heading=""
                  offerTitle={name}
                  tileInclusionsList={inclusions}
                  maxInclusionsCount={2}
                  offerURL={offerURL}
                />
              )}
            </VerticalSpacer>
            {descriptorsString && (
              <DescriptorsWrapper>
                <BodyTextBlock
                  variant="medium"
                  weight="normal"
                  startIcon={<LineBookmarkIcon size="S" />}
                >
                  {descriptorsString}
                </BodyTextBlock>
              </DescriptorsWrapper>
            )}
            {printDescriptorsString && (
              <PrintOnly>
                <BodyText variant="medium" weight="semi-bold">
                  {printDescriptorsString}
                </BodyText>
              </PrintOnly>
            )}
          </VerticalSpacer>
          <VerticalSpacer gap={16}>
            {variant === 'drawer' && tripContext && (
              <StructureLabel variant="information">
                <BodyTextBlock variant="small">Added to </BodyTextBlock>
                <TextLink
                  size="caption"
                  onClick={(e) => {
                    e.stopPropagation()
                    onClickTripName()
                  }}
                >
                  {tripContext.name}
                </TextLink>
              </StructureLabel>
            )}
            <OnlyIfChildren as={SavedBottomContent}>
              {priceAreaContent && (
                <div className="price-area">{priceAreaContent}</div>
              )}
              {ctaAreaContent && (
                <div className="cta-area">{ctaAreaContent}</div>
              )}
            </OnlyIfChildren>
          </VerticalSpacer>
        </MainContent>
      </Content>
    </BaseSavedOffer>
  )
}

export default memoWithNestedProps(SavedOfferCard, [
  'descriptors',
  'printDescriptors',
])
